import { Dispatch } from 'redux'
import { toast } from 'react-toastify'
const { createLogic } = require('redux-logic')

import history from '../../history'

import { File } from '../../lib/interfaces/File.interface'
import { ClaimInvoice } from '../../lib/interfaces/ClaimInvoice.interface'

import {
  actionTypes,
  payInvoiceError,
  InvoicePayAction,
  fetchInvoiceError,
  fetchInvoiceFiles,
  payInvoiceSuccess,
  createInvoiceError,
  deleteInvoiceError,
  InvoiceFetchAction,
  fetchInvoiceSuccess,
  InvoiceCreateAction,
  InvoiceDeleteAction,
  createInvoiceSuccess,
  deleteInvoiceSuccess,
  fetchInvoiceFilesError,
  fetchInvoicesListError,
  uploadInvoiceFileError,
  InvoiceFetchFilesAction,
  InvoicesFetchListAction,
  InvoiceUploadFileAction,
  fetchInvoiceFilesSuccess,
  fetchInvoicesListSuccess,
  uploadInvoiceFileSuccess,
  InvoiceAddTrackingAction,
  addInvoiceTrackingSuccess,
  addInvoiceTrackingError  
} from './actions'

import * as api from '../../lib/api/invoices'

type OnDoneType = () => void

const get = require('lodash/get')

const onFetchInvoicesListLogic = createLogic({
  type: actionTypes.INVOICES_FETCH_LIST,
  process({ action }: { action: InvoicesFetchListAction }, dispatch: Dispatch, done: OnDoneType) {
    api.getInvoices()
      .then((invoices: [ClaimInvoice]) => {
        dispatch(fetchInvoicesListSuccess(invoices))
      })
      .catch(err => dispatch(fetchInvoicesListError(err)))
      .then(done)
  }
})

const onFetchInvoiceLogic = createLogic({
  type: actionTypes.INVOICE_FETCH,
  process({ action }: { action: InvoiceFetchAction }, dispatch: Dispatch, done: OnDoneType) {
    const { next, invoiceId } = action.payload

    api.getInvoice(invoiceId)
      .then((invoice: ClaimInvoice) => {
        dispatch(fetchInvoiceSuccess(invoice))

        if (next) {
          next(null, invoice)
        }
      })
      .catch(err => {
        dispatch(fetchInvoiceError(err))

        if (next) {
          next(err)
        }
      })
      .then(done)
  }
})

const onCreateInvoiceLogic = createLogic({
  type: actionTypes.INVOICES_CREATE,
  process({ action }: { action: InvoiceCreateAction }, dispatch: Dispatch, done: OnDoneType) {
    api.createInvoice(action.payload.claimIds)
      .then((invoice: ClaimInvoice) => {
        dispatch(createInvoiceSuccess(invoice))

        toast.success('Invoice successfully created')
        history.push(`/invoices/${invoice.id}`)
      })
      .catch(err => {
        const errorMessage = get(err, 'response.data.error', 'Error creating invoice. Please try again')
        toast.error(errorMessage)
        dispatch(createInvoiceError(errorMessage))
      })
      .then(done)
  }
})

const onUploadInvoiceFileLogic = createLogic({
  type: actionTypes.INVOICES_UPLOAD_FILE,
  process({ action }: { action: InvoiceUploadFileAction }, dispatch: Dispatch, done: OnDoneType) {
    api.uploadInvoiceFile(action.payload.invoiceId, action.payload.file)
      .then(() => {
        dispatch(uploadInvoiceFileSuccess())

        const invoiceId = parseInt(`${action.payload.invoiceId}`, 10)
        dispatch(fetchInvoiceFiles(invoiceId))
      })
      .catch(err => {
        const errorMessage = get(err, 'response.data.error', 'Error uploading the file. Please try again')
        dispatch(uploadInvoiceFileError(errorMessage))
        toast.error(errorMessage)
      })
      .then(done)
  }
})

const onFetchInvoiceFilesLogic = createLogic({
  type: actionTypes.INVOICES_FETCH_FILES,
  process({ action }: { action: InvoiceFetchFilesAction }, dispatch: Dispatch, done: OnDoneType) {
    api.getInvoiceFiles(action.payload.invoiceId)
      .then((files: File[]) => {
        const invoiceId = parseInt(`${action.payload.invoiceId}`, 10)
        dispatch(fetchInvoiceFilesSuccess(files, invoiceId))
      })
      .catch(err => dispatch(fetchInvoiceFilesError(err)))
      .then(done)
  }
})

const onPayInvoiceLogic = createLogic({
  type: actionTypes.INVOICES_PAY,
  process({ action }: { action: InvoicePayAction }, dispatch: Dispatch, done: OnDoneType) {
    const { next, datePaid, invoiceId, checkNumber } = action.payload
    api.payInvoice(invoiceId, { datePaid, checkNumber })
      .then((invoice: ClaimInvoice) => {
        dispatch(payInvoiceSuccess(invoice))
        toast.success('Invoice successfully payed')

        if (next) {
          next(null, invoice)
        }
      })
      .catch(err => {
        dispatch(payInvoiceError(err))

        const errorMessage = get(err, 'response.data.error', 'Error paying invoice. Please try again')
        toast.error(errorMessage)
        
        if (next) {
          next(err)
        }
      })
      .then(done)
  }
})

const onDeleteInvoiceLogic = createLogic({
  type: actionTypes.INVOICES_DELETE,
  process({ action }: { action: InvoiceDeleteAction }, dispatch: Dispatch, done: OnDoneType) {
    const { next, invoiceId } = action.payload
    api.deleteInvoice(invoiceId)
      .then(() => {
        dispatch(deleteInvoiceSuccess(invoiceId))
        toast.success('Invoice successfully deleted')

        if (next) {
          next()
        }
      })
      .catch(err => {
        dispatch(deleteInvoiceError(err))

        const errorMessage = get(err, 'response.data.message', 'Error deleting invoice. Please try again')
        toast.error(errorMessage)

        if (next) {
          next(err)
        }
      })
      .then(done)
  }
})

const onUpdateInvoiceLogic = createLogic({
  type: actionTypes.INVOICES_ADD_TRACKING,
  process({ action }: { action: InvoiceAddTrackingAction }, dispatch: Dispatch, done: OnDoneType) {
    const { next, invoiceId, mailTracking } = action.payload
    api.updateInvoice(invoiceId, {mailTracking})
      .then((invoice) => {
        
        dispatch(addInvoiceTrackingSuccess(invoice))
        if (next) {
          next(null)
        }
      })
      .catch(err => {
        dispatch(deleteInvoiceError(err))

        const errorMessage = get(err, 'response.data.message', 'Error deleting invoice. Please try again')
        toast.error(errorMessage)

        if (next) {
          next(err)
        }
      })
  }
})

export const invoicesLogic = [
  onFetchInvoicesListLogic,
  onFetchInvoiceLogic,
  onCreateInvoiceLogic,
  onUploadInvoiceFileLogic,
  onFetchInvoiceFilesLogic,
  onPayInvoiceLogic,
  onDeleteInvoiceLogic,
  onUpdateInvoiceLogic
]



// WEBPACK FOOTER //
// ./src/store/invoices/logic.ts